import { ComponentProps, Fragment, ReactNode, useMemo, useState } from 'react';

import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import MuiTableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import Box from '../box/box';
import { styled } from '../styles/styles';
import Typography from '../typography/typography';
import EmptyBody from './empty-body';
import Filters, { FilterOptions } from './filters';
import { getColumnConfig, getColumnForRow } from './helpers';
import { AlignType, Column } from './types';

export type { Column };

export interface TableProps<Row extends Record<string, unknown>> {
  data: Row[];
  columns?: Column<Row>[];
  expandedComponent?: (row: Row) => ReactNode;
  emptyMessage?: string;
  tableContainerBackgroundColor?: string;
  tableHeaderRowBackgroundColor?: string;
  appliedFilters?: ComponentProps<typeof Filters>['appliedFilters'];
  onApplyFilter?: ComponentProps<typeof Filters>['onApplyFilter'];
  onClearFilter?: ComponentProps<typeof Filters>['onClearFilter'];
}

const shouldForwardProp = (propName: PropertyKey) =>
  propName !== 'backgroundColor';

const TableContainer = styled(MuiTableContainer, { shouldForwardProp })<{
  backgroundColor?: string;
}>`
  background-color: ${(props) =>
    props.backgroundColor || props.theme.background.white};
`;

const TableHeaderRow = styled(TableRow, { shouldForwardProp })<{
  backgroundColor?: string;
}>`
  background-color: ${(props) =>
    props.backgroundColor || props.theme.custom?.tableHeaderRowFallback};
`;

export const DataTable = <Row extends Record<string, unknown>>(
  props: TableProps<Row>
) => {
  const {
    data,
    columns = [],
    expandedComponent,
    emptyMessage,
    appliedFilters,
    onApplyFilter,
    onClearFilter,
    tableContainerBackgroundColor,
    tableHeaderRowBackgroundColor,
  } = props;

  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const head = useMemo(() => {
    return (
      <TableHeaderRow backgroundColor={tableHeaderRowBackgroundColor}>
        {columns.map((column) => {
          const align = get(column, 'align', 'left') as AlignType;
          const filtered = get(column, 'filtered');
          const filterType = get(column, 'filterType');
          const accessor = get(column, 'accessor') as string;
          const options = get(column, 'options', []) as FilterOptions;
          const width = get(column, 'width', undefined);
          return (
            <TableCell
              key={accessor}
              align={align}
              component="th"
              width={width}
            >
              <Box display="flex" alignItems="center">
                <Typography
                  whiteSpace="nowrap"
                  variant="body2"
                  fontWeight="bold"
                  fontSize={13}
                >
                  {column.label}
                </Typography>
                {filtered && (
                  <Filters
                    accessor={accessor}
                    label={column.label}
                    filterType={filterType}
                    options={options}
                    appliedFilters={appliedFilters}
                    onApplyFilter={onApplyFilter}
                    onClearFilter={onClearFilter}
                  />
                )}
              </Box>
            </TableCell>
          );
        })}
      </TableHeaderRow>
    );
  }, [
    appliedFilters,
    columns,
    onApplyFilter,
    onClearFilter,
    tableHeaderRowBackgroundColor,
  ]);

  const body = useMemo(() => {
    const columnKeys = columns.map((column) => column.accessor);
    if (!data) return;
    return data.map((row, rowId) => {
      const onExpand = () => {
        setExpandedRows((state) => ({
          ...state,
          [rowId]: !state[rowId],
        }));
      };
      return (
        <Fragment key={`row-${rowId}`}>
          <>
            <TableRow>
              {columnKeys.map((accessor, colId) => {
                const column = getColumnConfig(columns, colId);
                const isExpanded = expandedRows[rowId];
                const align = get(column, 'align', 'left') as AlignType;
                return (
                  <TableCell key={`row-${rowId}-cell-${colId}`} align={align}>
                    {getColumnForRow(
                      column,
                      row,
                      accessor,
                      isExpanded,
                      onExpand
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
            {expandedRows[rowId] && (
              <TableRow>
                <TableCell
                  style={{ paddingBottom: 0, paddingTop: 0 }}
                  colSpan={columns.length}
                >
                  <Box p={4}>{expandedComponent?.(row)}</Box>
                </TableCell>
              </TableRow>
            )}
          </>
        </Fragment>
      );
    });
  }, [columns, data, expandedComponent, expandedRows]);

  return (
    <TableContainer backgroundColor={tableContainerBackgroundColor}>
      <MuiTable>
        <TableHead>{head}</TableHead>
        <TableBody>{body}</TableBody>
      </MuiTable>
      {isEmpty(data) && !isEmpty(emptyMessage) && (
        <EmptyBody>{emptyMessage}</EmptyBody>
      )}
    </TableContainer>
  );
};

DataTable.EmptyTableBody = EmptyBody;
